import { BaseApiClient } from './base-client';
import type { ApiResponse } from './config';

// ---- Types ----

export interface ChatMessage {
  id?: string;
  message_id?: string;
  role: 'user' | 'assistant' | 'system';
  content: string;
  timestamp?: string;
  conversation_id?: string;
  /** Structured DESK/OMS event. System events are excluded from model history. */
  desk_event?: import('@/domains/chat-ai/lib/desk-action-protocol').DeskTimelineEvent;
  /** False when the reply did not come from a typed desk read or action. */
  grounded?: boolean;
  has_desk_action?: boolean;
}

export interface ChatConversation {
  id?: string;
  conversation_id?: string;
  title?: string;
  messages: ChatMessage[];
  created_at?: string;
  updated_at?: string;
  updatedAt?: string;
  /** Compressed older turns for the model window. Full transcript stays in messages. */
  session_summary?: string;
  session_compressed_at?: string;
  session_compressed_turns?: number;
}

export interface ChatResponse {
  conversation_id: string;
  message_id: string;
  message: string;
  intent?: string;
  config_applied?: boolean;
  timestamp: string;
}

export interface SendMessageRequest {
  message: string;
  conversation_id?: string;
  /** When true the ToolDispatcher routes the message before the LLM (positions, portfolio, etc.) */
  tool_dispatch?: boolean;
}

export interface ChatAnalysis {
  symbol: string;
  analysis_type: string;
  summary: string;
  signals: { signal: 'bullish' | 'bearish' | 'neutral'; confidence: number; factors: string[] };
  recommendations: string[];
}

// ---- Client ----

export class ChatAIClient extends BaseApiClient {
  // -- Status --

  async getStatus(): Promise<ApiResponse<{ status: string; model: string }>> {
    return this.get('/api/v1/chat/status');
  }

  // -- Messages (the core endpoint — ToolDispatcher routes commands before LLM) --

  async sendMessage(data: SendMessageRequest): Promise<ApiResponse<ChatResponse>> {
    return this.post('/api/v1/chat/message', data);
  }

  // -- Conversations --

  async getConversations(): Promise<ApiResponse<ChatConversation[]>> {
    return this.get('/api/v1/chat/conversations');
  }

  async getConversation(conversationId: string): Promise<ApiResponse<ChatConversation>> {
    return this.get(`/api/v1/chat/conversations/${conversationId}`);
  }

  /**
   * Create a conversation. The backend creates conversations implicitly when the
   * first message is sent without a conversation_id. We send a lightweight system
   * message to trigger creation and return the conversation metadata.
   */
  async createConversation(title?: string): Promise<ApiResponse<ChatConversation>> {
    const msg = title
      ? `[system] New conversation: ${title}`
      : '[system] Start a new conversation about my trading portfolio and market conditions.';
    const resp = await this.sendMessage({ message: msg });
    return {
      data: {
        conversation_id: resp.data?.conversation_id ?? '',
        messages: [],
        title,
      },
      status: resp.status,
      success: resp.success,
    } as ApiResponse<ChatConversation>;
  }

  /**
   * Delete a conversation.
   * NYI — backend does not expose DELETE /conversations/:id. Returns 501.
   */
  async deleteConversation(conversationId: string): Promise<ApiResponse<{ deleted: boolean }>> {
    return this.delete('/api/v1/chat/conversations/' + encodeURIComponent(conversationId));
  }

  // -- Analysis (wrapped sendMessage with structured prompts) --

  async analyzeSymbol(
    symbol: string,
    analysisType?: string,
  ): Promise<ApiResponse<ChatAnalysis>> {
    const type = analysisType ?? 'technical';
    const resp = await this.sendMessage({
      message: `Analyze ${symbol} using ${type} analysis. Return key signals, support/resistance levels, and short-term outlook.`,
      tool_dispatch: true,
    });
    return {
      data: {
        symbol,
        analysis_type: type,
        summary: resp.data?.message ?? '',
        signals: { signal: 'neutral', confidence: 0.5, factors: [] },
        recommendations: [],
      },
      status: resp.status,
      success: resp.success,
    } as ApiResponse<ChatAnalysis>;
  }

  async getMarketBrief(): Promise<ApiResponse<ChatResponse>> {
    return this.sendMessage({
      message:
        'Give me a brief market overview: major indices, sector performance, and key macro events today.',
      tool_dispatch: true,
    });
  }

  async getPortfolioInsights(): Promise<ApiResponse<ChatResponse>> {
    return this.sendMessage({
      message:
        'Analyze my portfolio: show positions, P&L, risk exposure, and suggest rebalancing actions.',
      tool_dispatch: true,
    });
  }

  // -- Config (backed by real endpoints) --

  async applyConfig(config: Record<string, unknown>): Promise<ApiResponse<ChatResponse>> {
    return this.post('/api/v1/chat/config/apply', config);
  }

  async getPendingConfig(): Promise<ApiResponse<{ changes: Record<string, unknown>[] }>> {
    return this.get('/api/v1/chat/config/pending');
  }
}
